import { Journey } from '@audibene/ta.utils-opportunity'
import { Grid, SelectChangeEvent } from '@mui/material'
import { differenceInMinutes, startOfMonth } from 'date-fns'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  ANY_RESOURCE_ID,
  ANY_INTERNAL_RESOURCE_ID,
  ANY_EXTERNAL_RESOURCE_ID,
  AppointmentType,
  Country,
  ResourceType,
  SearchParameters,
  appointmentDurations,
} from '../../../../../../constants'
import {
  useAppointment,
  useBlockedAppointmentType,
  useDateFormat,
  useEventTracking,
  useFormat,
  useIsFitterSchedulingMode,
  useRescheduleContext,
  useSearchParameters,
  useShop,
  useShouldRestrictToHAE,
  useTrialGateTracking,
} from '../../../../../../hooks'
import { AppointmentFormData, AppointmentTypeConfiguration, AppointmentTypes, Customer, TimeZones } from '../../../../../../types'
import {
  appointmentToTitle,
  isAnyResourceId,
  resourceTypeToDisplayText,
  sortByGroupingThenName,
  sortByName,
  toGatedAppointmentTypeOptions,
  uniq,
} from '../../../../../../util'
import { AppointmentTypeSubstitutedAlert } from '../../../../AppointmentTypeSubstitutedAlert'
import { getSubstituteAppointmentType, NO_APPOINTMENT_TYPES, TrialGateSurface } from '../../../../../../hooks/useTrialGate/trialGates'
import { Autocomplete, Select } from '../../../../../forms'
import { useSpecialist } from '../../../useSpecialist'
import { useTimeEntryMode } from '../../../useTimeEntryMode'
import { useAvailability } from '../../Providers/Availability'
import { useIsCabinOnlyConfiguration } from '../../useIsCabinOnlyConfiguration'
import { useResourcesByResourceConfigurations } from '../../useResourcesByResourceConfigurations'
import { CustomerTitle } from './CustomerTitle'
import { useRedirectToProviderRecommendationFlowIfNeeded } from './useRedirectToProviderRecommendationFlowIfNeeded'
import { filterResource } from 'util/filterResources'

interface Props {
  appointmentTypes: AppointmentTypes
  customer: Customer
  isReadOnly?: boolean
  locale: string
  timeZones: TimeZones
}

interface Option {
  group?: string
  name: string
  value: string
}

const makeIsUniqueDuration = () => {
  const uniqueDurations = new Set<number>()

  return ({ value }: Option) => {
    const duration = Number(value)

    if (uniqueDurations.has(duration)) {
      return false
    }

    uniqueDurations.add(duration)

    return true
  }
}

const makeToStandardOption =
  (formatMinutes: (value: number) => string, t: (key: string) => string) =>
  ([key, value]: [string, number]) => {
    const standardOptionKey = key as keyof typeof appointmentDurations

    if (standardOptionKey === 'UNKNOWN') {
      return {
        name: t('common.label.duration.unknown'),
        value: value.toString(),
      }
    }

    if (standardOptionKey === 'MULTIPLE') {
      return {
        name: t('common.label.duration.multiple'),
        value: value.toString(),
      }
    }

    return {
      name: formatMinutes(Number(value)),
      value: value.toString(),
    }
  }

const sortByDurationAscending = (a: Option, b: Option) => Number(a.value) - Number(b.value)

const generateAppointmentDurationOptions = ({
  durationInMinutes,
  formatMinutes,
  t,
}: {
  durationInMinutes: number | string
  formatMinutes: (value: number) => string
  t: (key: string) => string
}) => {
  const isUniqueDuration = makeIsUniqueDuration()
  const toStandardOption = makeToStandardOption(formatMinutes, t)

  const standardOptions: Option[] = Object.entries(appointmentDurations).map(toStandardOption)
  const customOption: Option = { name: formatMinutes(Number(durationInMinutes)), value: durationInMinutes.toString() }
  const allOptions: Option[] = [customOption, ...standardOptions].filter(isUniqueDuration).sort(sortByDurationAscending)

  return allOptions
}

const timeZonesToTimeZoneOptions = (timeZones: TimeZones): Option[] =>
  Object.values(timeZones)
    .reduce<Option[]>((acc, timeZone) => {
      const { alternative_name, cities } = timeZone
      const [city] = cities
      const option: Option = {
        name: `${alternative_name} / ${city}`,
        value: timeZone.name,
      }

      return [...acc, option]
    }, [])
    .sort(sortByName)

const Configuration = ({ appointmentTypes, customer, isReadOnly, timeZones }: Props) => {
  const { setValue, watch } = useFormContext<AppointmentFormData>()
  const { beManualMode } = useTimeEntryMode()
  const { setSpecialistType, specialist } = useSpecialist()
  const { shop } = useShop()
  const { t } = useTranslation()
  const isFitterSchedulingMode = useIsFitterSchedulingMode()
  const { data: availabilitiesResponse } = useAvailability()
  const { appointment } = useAppointment()
  const { formatUnit: formatMinutes } = useFormat({ unit: 'minute' })
  const resourceToHasIntegration = availabilitiesResponse?.resourceHasCalendarIntegration
  const dayAvailability = availabilitiesResponse?.dayAvailability

  const [clientStartDate, selectedCustomerTimeZone, durationInMinutes, journey, specialistId, type] = watch([
    'clientStartDate',
    'customer_timezone',
    'duration_in_minutes',
    'journey',
    'specialist_id',
    'type',
  ]) as [Date | undefined, string | undefined, number | undefined, Journey, string | undefined, AppointmentType]

  const searchParams = useSearchParameters()
  const shouldRestrictToHAE = useShouldRestrictToHAE(journey)
  const isGermany = customer.countryCode === Country.DE
  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(rescheduleContext?.reason)

  const {
    activeTrialGateTypes,
    blockedAppointmentTypes,
    isTrialDataUnknown,
    reasons: blockedAppointmentTypeReasons,
    trialEndDate,
    trialStatusSource,
  } = useBlockedAppointmentType()
  const { track: trackEvent } = useEventTracking()
  const { dateToISODateKey } = useDateFormat(selectedCustomerTimeZone as string)
  const dayKey = dateToISODateKey(clientStartDate as Date)
  const availabilities = dayAvailability?.[dayKey]?.[specialistId as string]
  const shopIds = shop?.id ? [shop.id] : undefined
  const defaultResourceId = searchParams[SearchParameters.RESOURCE_ID] as string | undefined
  const config = appointmentTypes?.[type as AppointmentType]

  const isCabinOnlyConfiguration = useIsCabinOnlyConfiguration()
  const isCabinSelectionDisabled = watch('isCabinSelectionDisabled') as boolean | undefined

  const { isFetching: areResourcesFetching, resources = [] } = useResourcesByResourceConfigurations({
    isActive: true,
    journey,
    resourceConfigurations: config?.resource_configurations,
    shopIds,
  })

  const getTimeZoneFromGroup = (unknownTimezone: string) =>
    Object.keys(timeZones).find((timezone) => timeZones[timezone].group?.includes(unknownTimezone)) ?? ''

  const isSelectedResourceAny = isAnyResourceId(specialistId as string)

  useRedirectToProviderRecommendationFlowIfNeeded({
    appointmentType: type,
    customerSalesforceId: customer.salesforceId,
    journey,
    opportunitySalesforceId: appointment?.opportunity_gid,
  })

  useEffect(() => {
    const title = appointmentToTitle({
      customer,
      type,
    })

    setValue('title', title)
  }, [type, customer, setValue])

  const onAppointmentTypeChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const newAppointmentType = event.target.value as AppointmentType
      const newConfig = appointmentTypes?.[newAppointmentType]

      setValue('clientStartDate', null)
      setValue('clientStartTime', null)
      setValue('duration_in_minutes', newConfig?.duration_in_minutes)
      setValue('month_date', startOfMonth(new Date()))
      setValue('specialist_id', defaultResourceId ?? '')
      setValue('resource_ids', [])
      setValue('start_date', null)
      setValue('type', newConfig?.appointment_type)
    },
    [appointmentTypes, defaultResourceId, setValue]
  )

  const onResourceChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const selectedValue = event.target.value as string
      const isAnyoneSelected = isAnyResourceId(selectedValue)

      setValue('clientStartTime', null)
      setValue('start_date', null)
      // Set resource_ids to ['*'] when "Anyone" is selected to prevent firstAvailability override
      setValue('resource_ids', isAnyoneSelected ? [ANY_RESOURCE_ID] : [])
    },
    [setValue]
  )

  useEffect(() => {
    if (isSelectedResourceAny) {
      beManualMode(false)
    }
  }, [isSelectedResourceAny])

  const onCustomerTimeZoneChange = useCallback(
    (timeZone: string) => {
      setValue('clientStartDate', null)
      setValue('clientStartTime', null)
      setValue('month_date', startOfMonth(new Date()))
      setValue('specialist_id', defaultResourceId ?? '')
      setValue('resource_ids', [])
      setValue('start_date', null)
      setValue('customer_timezone', timeZone)
    },
    [appointmentTypes, setValue]
  )

  // Unlike the schedule form, this surface does not always offer the appointment type: fitter
  // scheduling mode renders the dropdown only for Germany, and everywhere else it is read-only
  // whenever the caller says so - a resource_id deep link, for instance. Nothing the provider
  // cannot pick may be disabled or counted, so the gate folds into a single value both the options
  // and the analytics read.
  //
  // Rescheduling is excluded outright rather than left to isReadOnly. The gate does not apply there
  // (useScheduleFormDefaultValues does not substitute either, since it would reclassify an existing
  // appointment), and the fitter/Germany branch below renders its Select without `disabled`, so
  // read-only alone would not hold it back on that path.
  const isAppointmentTypeSelectable = !isRescheduling && (isFitterSchedulingMode ? isGermany : !isReadOnly)
  const blockedAppointmentTypesOnThisSurface = isAppointmentTypeSelectable ? blockedAppointmentTypes : NO_APPOINTMENT_TYPES

  // shouldRestrictToHAE is the consultant restriction: only offer HAE for consultant + NETWORK_STANDARD.
  const appointmentTypeOptions = useMemo(
    () =>
      toGatedAppointmentTypeOptions({
        appointmentTypes,
        blockedAppointmentTypes: blockedAppointmentTypesOnThisSurface,
        reasons: blockedAppointmentTypeReasons,
        shouldRestrictToHAE,
      }),
    [appointmentTypes, blockedAppointmentTypesOnThisSurface, blockedAppointmentTypeReasons, shouldRestrictToHAE]
  )

  // Taken from the rendered options rather than re-deriving the conditions, so it cannot drift from
  // what the user is shown.
  const offeredAppointmentTypes = useMemo(
    () => (isAppointmentTypeSelectable ? appointmentTypeOptions.map((option) => option.value) : []),
    [appointmentTypeOptions, isAppointmentTypeSelectable]
  )

  useTrialGateTracking({
    activeTrialGateTypes,
    blockedAppointmentTypes: blockedAppointmentTypesOnThisSurface,
    isTrialDataUnknown,
    offeredAppointmentTypes,
    surface: TrialGateSurface.CONSULTANT_CONFIGURATION,
    trackEvent,
    trialEndDate,
    trialStatusSource,
  })

  // Disabling the option only constrains the next pick; the value already in the form was chosen
  // earlier. If the blocked set grows while this page is open - LaunchDarkly streams the flag on, or
  // useNow crosses the trial boundary - the field would sit on a greyed-out value that still
  // submits, since the schema checks enum membership only. ScheduleForm carries the same steer for
  // the same reason; this surface reached it through the widened blocked set (PX-3749).
  //
  // Driven by blockedAppointmentTypesOnThisSurface, not the raw set, so a surface that is not
  // offering the choice at all - rescheduling, read-only - never rewrites the value underneath it.
  const liveTypeSubstitute = getSubstituteAppointmentType(type as AppointmentType, blockedAppointmentTypesOnThisSurface)
  const isLiveTypeSubstituteOffered = appointmentTypeOptions.some((option) => option.value === liveTypeSubstitute)

  useEffect(() => {
    if (!liveTypeSubstitute || !isLiveTypeSubstituteOffered) return

    // The same slot reset onAppointmentTypeChange performs: the time was picked against the old
    // type's availability and duration, neither of which carries over.
    setValue('clientStartDate', null)
    setValue('clientStartTime', null)
    setValue('duration_in_minutes', appointmentTypes?.[liveTypeSubstitute]?.duration_in_minutes)
    setValue('month_date', startOfMonth(new Date()))
    setValue('specialist_id', defaultResourceId ?? '')
    setValue('resource_ids', [])
    setValue('start_date', null)
    setValue('type', liveTypeSubstitute, { shouldDirty: true, shouldValidate: true })
  }, [appointmentTypes, defaultResourceId, isLiveTypeSubstituteOffered, liveTypeSubstitute, setValue])

  let resourceOptions = resources
    .filter(filterResource({ isCabinOnlyConfiguration, resourceHasCalendarIntegration: resourceToHasIntegration }))
    .map(({ id, name, type }) => ({
      group: t(resourceTypeToDisplayText[type]),
      name: name as string,
      value: id,
    }))
    .sort(sortByGroupingThenName)

  if (resourceOptions.length > 0) {
    const specialistTitleFallback = t('common.label.specialistType.specialist.title')
    const groupTitle = specialist?.title ? t(specialist.title) : specialistTitleFallback
    const anyoneResourceOption = {
      group: (isCabinOnlyConfiguration
        ? t('common.label.resource.anyCabin')
        : t('common.label.resource.anyWithTitle', { title: groupTitle })) as string,
      name: isCabinOnlyConfiguration ? t('common.label.resource.anyCabin') : t('common.label.resource.anyone'),
      value: ANY_RESOURCE_ID,
    }

    const hasInternalProviders = resources.some((r) => r.type === ResourceType.FITTER_INTERNAL)
    const hasExternalProviders = resources.some((r) => r.type === ResourceType.FITTER)
    const hasBothProviderTypes = hasInternalProviders && hasExternalProviders

    const anyOptions = [
      anyoneResourceOption,
      ...(hasBothProviderTypes && !isCabinOnlyConfiguration
        ? [
            { group: anyoneResourceOption.group, name: t('common.label.anyInternalProvider'), value: ANY_INTERNAL_RESOURCE_ID },
            { group: anyoneResourceOption.group, name: t('common.label.anyExternalProvider'), value: ANY_EXTERNAL_RESOURCE_ID },
          ]
        : []),
    ]

    resourceOptions = [...anyOptions, ...resourceOptions]
  }

  const resourceTypes =
    resources?.map(({ type }) => {
      if ([ResourceType.FITTER, ResourceType.FITTER_INTERNAL].includes(type)) {
        return ResourceType.FITTER
      }

      return type
    }) ?? ([] as string[])

  const uniqueResourceTypes = uniq(resourceTypes)

  useEffect(() => {
    if (isCabinOnlyConfiguration) {
      setSpecialistType(ResourceType.CABIN)
      return
    }

    const hasMultipleResourceTypes = uniqueResourceTypes.length > 1

    if (hasMultipleResourceTypes) {
      setSpecialistType('SPECIALIST')

      return
    }

    setSpecialistType(uniqueResourceTypes[0])
  }, [isCabinOnlyConfiguration, setSpecialistType, uniqueResourceTypes])

  // In fitter scheduling mode, default to "Anyone" when resource_id is missing or invalid
  useEffect(() => {
    if (!isFitterSchedulingMode || areResourcesFetching || !resources) return

    const isValidResourceId = defaultResourceId && resources.some(({ id }) => id === defaultResourceId)

    // Only set to ANY_RESOURCE_ID if we don't have a valid resource_id from URL
    // and specialist_id is empty (not yet selected by user)
    if (!isValidResourceId && !specialistId) {
      setValue('specialist_id', ANY_RESOURCE_ID)
      setValue('resource_ids', [ANY_RESOURCE_ID])
    }
  }, [isFitterSchedulingMode, areResourcesFetching, resources, defaultResourceId, specialistId, setValue])

  const customerDefaultTimeZone = customer?.timezone

  const normalizedCustomerDefaultTimezone = useMemo(
    () =>
      timeZones[customerDefaultTimeZone as string] ? customerDefaultTimeZone : getTimeZoneFromGroup(customerDefaultTimeZone as string),
    [customerDefaultTimeZone]
  )

  const rawFitterTimeZone = resources.find(({ id }) => id === specialistId)?.timezone ?? ''

  const fitterTimezone = isSelectedResourceAny
    ? selectedCustomerTimeZone
    : timeZones[rawFitterTimeZone]
    ? rawFitterTimeZone
    : getTimeZoneFromGroup(rawFitterTimeZone)

  useEffect(() => setValue('fitter_timezone', fitterTimezone), [fitterTimezone, setValue])

  const timeZoneOptions = timeZonesToTimeZoneOptions(timeZones)

  const timezones = timeZoneOptions.map(({ name, value }) => ({
    name: `${normalizedCustomerDefaultTimezone === value ? `• ${name}` : name}`,
    value,
  }))

  const fitterTimeZoneLabel =
    specialist?.title && !isSelectedResourceAny
      ? t('common.label.timezoneWithSpecialistPrefix', { specialist: t(specialist?.title ?? '') })
      : t('common.label.specialistTimezone')

  const specialistTimezones = isSelectedResourceAny
    ? [
        {
          name: t('common.label.duration.multiple'),
          value: fitterTimezone,
        },
      ]
    : timezones

  let hasMultipleDurations = false
  const durationFromConfig: AppointmentTypeConfiguration | undefined = appointmentTypes?.[type as AppointmentType]
  let duration: string | number = durationFromConfig?.duration_in_minutes || appointmentDurations.UNKNOWN

  if (availabilities && availabilities.length > 0) {
    const [availability] = availabilities
    const durationInMinutes = differenceInMinutes(availability.date_end, availability.date_start)

    hasMultipleDurations = availabilities.some((availability) => {
      const thisDuration = differenceInMinutes(availability.date_end, availability.date_start)

      return durationInMinutes !== thisDuration
    })

    if (!hasMultipleDurations) {
      duration = durationInMinutes.toString()
    }
  }

  useEffect(() => {
    switch (true) {
      case hasMultipleDurations:
        setValue('duration_in_minutes_temp', appointmentDurations.MULTIPLE)
        break
      case Boolean(duration):
        setValue('duration_in_minutes_temp', Number(duration))

        // Only update the actual duration when the duration is a valid duration
        setValue('duration_in_minutes', Number(duration))
        break
      default:
        setValue('duration_in_minutes_temp', appointmentDurations.UNKNOWN)
        break
    }
  }, [duration, hasMultipleDurations, setValue])

  const appointmentDurationOptions = useMemo(
    () =>
      generateAppointmentDurationOptions({
        durationInMinutes: durationInMinutes ?? appointmentDurations.UNKNOWN,
        formatMinutes,
        t,
      }),
    [durationInMinutes, formatMinutes, t]
  )

  const isActuallyReadOnly = isReadOnly || !resourceOptions.length
  const isSpecialistSelectDisabled = isCabinSelectionDisabled || (!isFitterSchedulingMode && isActuallyReadOnly)

  return (
    <Grid container rowSpacing={2}>
      <AppointmentTypeSubstitutedAlert blockedAppointmentTypes={blockedAppointmentTypes} selectedAppointmentType={type} />
      {isFitterSchedulingMode && isGermany ? (
        <Grid item xs={12}>
          <Select
            defaultValue=""
            label={t('common.label.typeOfAppointment')}
            name="type"
            onChange={onAppointmentTypeChange}
            options={appointmentTypeOptions}
          />
        </Grid>
      ) : (
        <></>
      )}
      {!isFitterSchedulingMode && (
        <>
          <Grid item xs={12}>
            <CustomerTitle customer={customer} />
          </Grid>
          <Grid item xs={12}>
            <Select
              defaultValue=""
              disabled={isReadOnly}
              label={t('common.label.typeOfAppointment')}
              name="type"
              onChange={onAppointmentTypeChange}
              options={appointmentTypeOptions}
            />
          </Grid>
          {customer.countryCode !== Country.DE && (
            <Grid item xs={12}>
              <Select
                disabled
                label={t('common.label.appointmentDuration')}
                name="duration_in_minutes_temp"
                onChange={() => null}
                options={appointmentDurationOptions}
                readOnly
              />
            </Grid>
          )}
        </>
      )}
      <Grid item xs={12}>
        <Select
          defaultValue=""
          disabled={isSpecialistSelectDisabled}
          label={t(specialist?.title ?? '')}
          name="specialist_id"
          onChange={onResourceChange}
          options={resourceOptions}
        />
      </Grid>

      {customer.countryCode !== Country.DE && (
        <Grid item xs={12}>
          <Autocomplete
            defaultValue={normalizedCustomerDefaultTimezone}
            disableClearable
            label={t('common.label.customerTimezone')}
            name="customer_timezone"
            onChange={onCustomerTimeZoneChange}
            options={timezones}
          />
        </Grid>
      )}

      {!isFitterSchedulingMode && customer.countryCode !== Country.DE && (
        <Grid item xs={12}>
          <Select defaultValue="" disabled label={fitterTimeZoneLabel} name="fitter_timezone" options={specialistTimezones} />
        </Grid>
      )}
    </Grid>
  )
}

export { Configuration as Content }
